
import { Component, Prop, Ref, Vue } from "vue-property-decorator"
import { Getter } from "vuex-class"

import { CaseDetails as CaseDetailsType, CaseField, TemplateDetails } from "@/types"

import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import ReopenCaseModal from "@/components/CaseDetails/ReopenCaseModal.vue"
import Toolbar, { ToolbarButton } from "@/components/New/Page/PageComponents/Toolbar.vue"
import DownloadTemplateSelect from "@/components/Fields/DownloadTemplateSelect.vue"
import SendEmailModal from "@/components/CaseDetails/SendEmailModal.vue"

import api from "@/services/api"
import CaseFile from "@/types/caseFile"
import { CurrentUser } from "@/types/user"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import { TenantFeatures } from "@/types/tenantFeatures"

import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"
import { getCanEditClosedCase, getCanReopenClosedCase, reopenCase as reopenCurrentCase } from "@/scripts/caseDetails"
import { generateQrCodeDataUrl } from "@/scripts/qrCode"
import CaseTimelineModal from "./CaseTimelineModal.vue"

enum CaseButtons {
	Merge = "merge",
	GenerateQr = "generate-qr",
	DownloadPdf = "download-pdf",
	SendEmail = "send-email",
	Reopen = "reopen",
	ChangeClosed = "change-close",
	SetSensitive = "make-sensitive",
	InsightHide = "hide-from-insights",
	Delete = "delete",
	Close = "close",
	Timeline = "timeline",
	Print = "print"
}

@Component({
	components: {
		"toolbar": Toolbar,
		"case-merge-modal": CaseMergeModal,
		"loader": SkeletonLoader,
		"case-timeline-modal": CaseTimelineModal,
		"download-template-select": DownloadTemplateSelect,
		"send-email-modal": SendEmailModal,
		"reopen-case-modal": ReopenCaseModal
	}
})
export default class CaseToolbar extends Vue {
	@Ref() caseMergeModal!: CaseMergeModal

	@Prop({ required: true, type: Object })
	public currentCase!: CaseDetailsType

	@Prop({ default: null, type: Object })
	public caseTemplate!: TemplateDetails | null

	@Prop({ default: false, type: Boolean })
	public canEditCurrentCase!: boolean

	@Prop({ type: Boolean, default: false })
	public canCreateComments!: boolean

	@Prop({ default: () => [], type: Array })
	public caseFiles!: CaseFile[]

	@Getter("getMe")
	public me!: CurrentUser

	@Getter("getCaseName") caseName!: string;
	@Getter("getRecordName") recordName!: string;

	@Getter("isFeatureEnabled") featureEnabled!: (feature: TenantFeatures) => boolean

	hideFromInsightsModalShown: boolean = false
	markSensitiveModalShown: boolean = false
	deleteCaseModalShown: boolean = false
	mergeModalShown: boolean = false
	qrModalShown: boolean = false
	downloadModalShown: boolean = false
	isSendEmailModalVisible: boolean = false
	closeModalShown: boolean = false
	reopenModalShown: boolean = false
	collapsed: boolean = false
	qrImage: string | null = null

	deleteConfirmText: string | null = null

	get caseRecordName (): string {
		return this.currentCase.isCase
			? this.caseName
			: this.recordName
	}

	get recordSlashCase (): string {
		return this.caseName === this.recordName
			? this.caseName
			: `${this.recordName}/${this.caseName}`
	}

	get canMakeClosedChanges (): boolean {
		return getCanEditClosedCase(this.caseTemplate, this.me)
	}

	/**
	 * Checks if something is preventing the case from being closed
	 * @return true if all case fields validated, all required actions completed and no user roles require location, false otherwise
	 */
	public get canClose (): boolean {
		return (!this.caseFieldsNotValidated || this.caseFieldsNotValidated.length === 0) && this.caseActionsValidated
	}

	public get isPdfTemplateEnabled (): boolean {
		return this.featureEnabled(TenantFeatures.PdfTemplates)
	}

	private get isSendEmailEnabled (): boolean {
		return this.featureEnabled(TenantFeatures.SendEmail)
	}

	private get templateCanReopenClosed (): boolean {
		return getCanReopenClosedCase({
			reopenClosedCasesEnabled: this.featureEnabled(TenantFeatures.ReopenClosedCases),
			template: this.caseTemplate,
			me: this.me
		})
	}

	get toolbarButtons (): ToolbarButton[] {
		const buttons: ToolbarButton[] = []

		if (!this.currentCase || !this.currentCase.id) return buttons

		if (this.currentCase.isClosed) {
			if (this.canMakeClosedChanges && !this.canEditCurrentCase) {
				buttons.push({
					key: CaseButtons.ChangeClosed,
					icon: "far fa-edit",
					label: "Make changes",
					tooltip: `Make changes to this closed ${this.caseRecordName}`
				})
			}

			if (this.templateCanReopenClosed && !this.featureEnabled(TenantFeatures.SidebarCloseButton)) {
				buttons.push({
					key: CaseButtons.Reopen,
					icon: "far fa-lock-open",
					label: "Reopen",
					tooltip: `Reopen ${this.caseRecordName}`
				})
			}
		} else if (this.canEditCurrentCase && !this.featureEnabled(TenantFeatures.SidebarCloseButton)) {
			buttons.push({
				key: CaseButtons.Close,
				icon: "fa-solid fa-box-check",
				label: "Close",
				tooltip: this.closeCaseButtonToolTipTitle,
				disabled: !this.canClose
			})
		}

		buttons.push({
			key: CaseButtons.Timeline,
			icon: "far fa-list",
			label: "Timeline",
			tooltip: `View ${this.caseRecordName} timeline`
		})

		buttons.push({
			key: CaseButtons.GenerateQr,
			icon: "far fa-qrcode",
			label: "QR code",
			tooltip: "View QR code"
		})

		if (!this.currentCase.isSensitive) {
			buttons.push({
				key: CaseButtons.SetSensitive,
				icon: "far fa-shield",
				label: "Mark Sensitive",
				tooltip: `Mark ${this.caseRecordName} as sensitive`,
				variant: "info"
			})
		}

		buttons.push({
			key: CaseButtons.Print,
			icon: "far fa-print",
			label: "Print",
			tooltip: `Print ${this.caseRecordName}`,
			variant: "info"
		})

		if (this.isSendEmailEnabled) {
			buttons.push({
				key: CaseButtons.SendEmail,
				icon: "far fa-envelope",
				label: "Send Email",
				tooltip: `Send ${this.caseRecordName} by email`,
				variant: "info"
			})
		}

		if (this.isPdfTemplateEnabled) {
			buttons.push({
				key: CaseButtons.DownloadPdf,
				icon: "far fa-file-pdf",
				label: "Download Report",
				tooltip: `Download ${this.caseRecordName} report`,
				variant: "info"
			})
		}

		if (this.canEditCurrentCase) {
			buttons.push({
				key: CaseButtons.Merge,
				icon: "far fa-merge",
				label: "Merge into",
				tooltip: `Merge this ${this.caseRecordName} into another ${this.recordSlashCase}`
			})

			if (this.currentCase.hiddenFromInsights) {
				buttons.push({
					key: CaseButtons.InsightHide,
					icon: "far fa-eye",
					label: "Show in insights",
					tooltip: `Show this ${this.caseRecordName} in insights reports`
				})
			} else {
				buttons.push({
					key: CaseButtons.InsightHide,
					icon: "far fa-eye-slash",
					label: "Hide from insights",
					tooltip: `Hide this ${this.caseRecordName} from insights reports`
				})
			}

			buttons.push({
				key: CaseButtons.Delete,
				icon: "far fa-trash",
				label: "Delete",
				tooltip: `Delete ${this.caseRecordName}`,
				variant: "danger"
			})
		}

		return buttons
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		return allFields.filter(field => this.fieldRequired(field))
	}

	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && !isCase) || (action.requiredCase && isCase)) &&
															action.completedBy === null)
	}

	public get closeCaseButtonToolTipTitle (): string {
		if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0 && !this.caseActionsValidated) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " and actions to be completed."
		} else if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " to be completed."
		} else if (!this.caseActionsValidated) {
			return "All field actions required"
		}

		return `Close ${this.caseRecordName}`
	}

	async toolbarClick (buttonKey: CaseButtons) {
		switch (buttonKey) {
			case CaseButtons.ChangeClosed:
				this.$emit("editClosed")
				break
			case CaseButtons.Merge:
				if (this.caseMergeModal) {
					this.caseMergeModal.open()
				}
				break
			case CaseButtons.GenerateQr:
				await this.loadQrCode()
				this.qrModalShown = true
				break
			case CaseButtons.SetSensitive:
				this.markSensitiveModalShown = true
				break
			case CaseButtons.InsightHide:
				if (this.currentCase.hiddenFromInsights) {
					await this.hideFromInsights(false)
					break
				}
				this.hideFromInsightsModalShown = true
				break
			case CaseButtons.Delete:
				this.deleteCaseModalShown = true
				break
			case CaseButtons.Close:
				if (this.canClose) {
					this.closeModalShown = true
				}
				break
			case CaseButtons.Timeline:
				if (this.currentCase) {
					const caseTimelineModal = this.$refs.caseTimelineModal as unknown as CaseTimelineModal | undefined
					if (caseTimelineModal) {
						await caseTimelineModal.open()
					}
				}
				break
			case CaseButtons.Print:
				window.print()
				break
			case CaseButtons.DownloadPdf:
				this.downloadModalShown = true
				break
			case CaseButtons.SendEmail:
				this.isSendEmailModalVisible = true
				break
			case CaseButtons.Reopen:
				this.reopenModalShown = true
				break
		}
	}

	private async reopenCase (): Promise<void> {
		await reopenCurrentCase(this.currentCase)
		this.reopenModalShown = false
	}

	private async loadQrCode (): Promise<void> {
		try {
			this.qrImage = await generateQrCodeDataUrl(location.href)
		} catch (err) {
			console.error("Error generating QR code:", err)
			this.qrImage = null
		}
	}

	public async hideFromInsights (hide: boolean) {
		if (!this.currentCase.id) {
			return
		}

		this.$set(this.currentCase!, "hiddenFromInsights", hide)
		await api.saveCase(this.currentCase)

		this.hideFromInsightsModalShown = false
	}

	public async deleteCase () {
		if (this.currentCase.id &&
			((this.currentCase.title
				? this.deleteConfirmText === this.currentCase.title
				: this.deleteConfirmText === "delete"))
		) {
			await api.deleteCase(this.currentCase.id!)

			// Navigate to dashboard
			await this.$router.push("/cases")
		}
	}

	public async markSensitive () {
		this.$emit("markSensitive")
		this.markSensitiveModalShown = false
	}

	/**
	 * @returns false if the field is valid, true if field is inaccessible, not required, or has a value set
	 */
	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.caseFiles.filter(caseFile => caseFile.fieldId === id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private confirmClose () {
		this.$emit("closeCase")
		this.closeModalShown = false
	}
}
